import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, post, type SessionUser } from '@agribot/polygon-checks-ui'

/**
 * Signing in with an access token.
 *
 * The second of the two ways into this API: a root key provisions a user and
 * returns a long-lived token, and that token is what the user authenticates
 * with. A partner's application passes its own to the component as
 * `getToken`; this is our application doing the same for somebody signing in
 * with one — anyone testing what a provisioned user can actually see.
 *
 * **It is kept in localStorage, which is not as safe as the session cookie.**
 * The cookie is httpOnly and script cannot read it; this can be read by any
 * script that gets into the page. That is the trade for a credential the
 * browser was never given directly, and it is why the sign-in screen says so
 * rather than presenting the two as equivalent. A token is revocable —
 * `POST /users/{id}/tokens` — which is what makes it an acceptable trade.
 */
const KEY = 'pc_access_token'

/** Storage throws in some privacy modes; a missing token is not an error. */
function read(): string | null {
  try {
    return window.localStorage.getItem(KEY)
  } catch {
    return null
  }
}

export function setAccessToken(token: string | null): void {
  try {
    if (token) window.localStorage.setItem(KEY, token)
    else window.localStorage.removeItem(KEY)
  } catch {
    // Not fatal: the token still applies to this page for as long as it lives.
  }
  current = token
}

export const getAccessToken = (): string | null => current

let current: string | null = null

/**
 * Reads the stored token, once at startup and before anything fetches. The
 * application hands `getAccessToken` to the component as `getToken`, which is
 * asked per request, so signing in or out takes effect on the next call.
 */
export function loadAccessToken(): void {
  current = read()
}

/** A token, not a session cookie, is what this browser is carrying. */
export const hasAccessToken = (): boolean => current !== null

/**
 * Signing out has to end both credentials.
 *
 * Clearing the cookie while leaving a token in localStorage would put the
 * person straight back in, which does not look like signing out to them.
 */
export function useLogout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      setAccessToken(null)
      return post<{ ok: true }>('/auth/logout').catch(() => ({ ok: true as const }))
    },
    onSuccess: () => qc.clear(),
  })
}

/**
 * Adopts an access token, after checking it is one.
 *
 * Verified against `/auth/me` before it is kept: a typo pasted into
 * localStorage would otherwise leave every screen answering 401 with
 * nothing to point at.
 */
export function useSignInWithToken() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (token: string) => {
      const trimmed = token.trim()
      setAccessToken(trimmed)
      try {
        // Bounded: a wrong or firewalled address can hang for minutes
        // rather than refuse, and the screen would say "Checking…" all along.
        return await api<{ user: SessionUser }>('/auth/me', { signal: AbortSignal.timeout(15_000) })
      } catch (err) {
        setAccessToken(null)
        throw err
      }
    },
    onSuccess: () => qc.invalidateQueries(),
  })
}

/**
 * Signing out, outside React: for the application's `onSignOut`. Ends both
 * credentials, then reloads at the root so nothing rendered from the old
 * identity survives.
 */
export async function signOut(): Promise<void> {
  setAccessToken(null)
  await post('/auth/logout').catch(() => undefined)
  window.location.assign('/')
}

